import { CanvaSX, renderChildren } from '@lib/canvasx/canvasx';
import { isFillStruct } from '@lib/canvasx/shapes';
import { calculateDimensionsForProps } from '@lib/canvasx/util';

const START_ARROW_WIDTH = 16;
const END_ARROW_WIDTH = 20;

// The START_ARROW_PATH and END_ARROW_PATH SVGs have a height of 50
const SVG_HEIGHT = 50;

const hasStartArrow = (type: ProgressStepProps['type']) => {
  return type === 'middle' || type === 'end';
};

const hasEndArrow = (type: ProgressStepProps['type']) => {
  return type === 'middle' || type === 'start';
};

// Helper functions to manually draw custom svgs. drawImage and something like
// ctx.fill(new Path2D(path)) left sub-pixel rendering artifacts
const drawStartArrow = (ctx: CanvasRenderingContext2D) => {
  ctx.beginPath();
  ctx.moveTo(4.82656, 9.50105);
  ctx.bezierCurveTo(1.838, 5.34165, 0.34372, 3.26196, 1.18017, 1.63098);
  ctx.bezierCurveTo(2.01663, 0, 4.57749, 0, 9.69921, 0);
  ctx.lineTo(18, 0);
  ctx.lineTo(18, 50);
  ctx.lineTo(9.69921, 50);
  ctx.bezierCurveTo(4.57749, 50, 2.01663, 50, 1.18017, 48.369);
  ctx.bezierCurveTo(0.34372, 46.738, 1.838, 44.6583, 4.82655, 40.499);
  ctx.lineTo(12.6087, 29.6681);
  ctx.bezierCurveTo(14.2277, 27.4147, 15.0372, 26.288, 15.0372, 25);
  ctx.bezierCurveTo(15.0372, 23.712, 14.2277, 22.5853, 12.6087, 20.3319);
  ctx.lineTo(4.82656, 9.50105);
  ctx.closePath();
};

const drawEndArrow = (ctx: CanvasRenderingContext2D) => {
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(3.02701, 0, 5.83377, 1.58245, 7.40055, 4.17242);
  ctx.lineTo(18.3175, 22.2187);
  ctx.bezierCurveTo(19.3519, 23.9286, 19.3519, 26.0714, 18.3175, 27.7813);
  ctx.lineTo(7.40055, 45.8276);
  ctx.bezierCurveTo(5.83377, 48.4176, 3.02701, 50, 0, 50);
  ctx.lineTo(0, 0);
  ctx.closePath();
};

/**
 * Draws a progress step. The progress step can have a start and/or end arrow
 * and a single child (for now).
 *
 * The progress step calculates the width of the children adds the width of the
 * start/end arrow components as necessary. It will fill the start, "child rect",
 * and end component paths. Then it will shift the children after the shifting them
 * after the start component as necessary
 */
type DefaultRectProps = Omit<ShapeStruct, 'width' | 'height'>;
export type ProgressStepProps = {
  type: 'start' | 'middle' | 'end';
} & Partial<ShapeStruct>;
export const ProgressStep: CanvaSXComponent<
  ProgressStepProps,
  DefaultRectProps
> = function (props) {
  const { ctx } = CanvaSX.context;
  let { children, width, height, type = 'start', fill = 'black' } = props;

  if (typeof width !== 'number' || typeof height !== 'number') {
    const dimensions = calculateDimensionsForProps(props);
    if (typeof width !== 'number') {
      width = dimensions.width;
    }
    if (typeof height !== 'number') {
      height = dimensions.height;
    }
  }

  if (hasStartArrow(props.type)) {
    width -= START_ARROW_WIDTH;
  }
  if (hasEndArrow(props.type)) {
    width -= END_ARROW_WIDTH;
  }

  const fillColor = isFillStruct(fill) ? fill.color : fill;
  const opacity = isFillStruct(fill) ? fill.opacity ?? 1 : 1;

  ctx.save();
  const scaleY = height / SVG_HEIGHT;
  ctx.fillStyle = fillColor;
  ctx.globalAlpha = opacity;
  let rectOffset = 0;
  const radius = 8;

  ctx.beginPath();

  if (hasStartArrow(type)) {
    ctx.save();
    ctx.scale(1, scaleY);
    drawStartArrow(ctx);
    ctx.restore();
    rectOffset = START_ARROW_WIDTH;
  }

  // Draw the rect for the child content. If this is a start step, draw a rect
  // with rounded corners on the left side
  if (type === 'start') {
    ctx.roundRect?.(0, 0, width, height, radius);
    ctx.rect(radius, 0, width - radius, height);
  } else if (type === 'end') {
    // Draw the rect for the child content
    ctx.roundRect?.(rectOffset, 0, width, height, radius);
    ctx.rect(rectOffset, 0, width - radius, height);
  } else {
    ctx.rect(rectOffset, 0, width, height);
  }

  if (hasEndArrow(type)) {
    ctx.save();
    ctx.scale(1, scaleY);
    ctx.translate(width + rectOffset, 0);
    drawEndArrow(ctx);
    ctx.restore();
  }
  ctx.fill();
  ctx.restore();

  renderChildren(children);

  return null;
};

ProgressStep.calculateDimensions = props => {
  let { width, height } = props;

  if (typeof width !== 'number' || typeof height !== 'number') {
    const dimensions = calculateDimensionsForProps(props);
    if (typeof width !== 'number') {
      width = dimensions.width;
    }
    if (typeof height !== 'number') {
      height = dimensions.height;
    }
  }

  return { width, height };
};
